/**
 * Client-side image optimization for the CLI.
 *
 * Ports the web Asset Studio codec policy to Node, on by default before upload:
 *   - cap the longest edge at 2000 px (never upscale, preserve aspect ratio),
 *   - PNG → resized PNG (lossless; keeps transparency / hard edges),
 *   - SVG / AVIF / HEIC / HEIF / animated GIF → passthrough (bytes unchanged),
 *   - everything else (JPEG, WebP, BMP, TIFF, static GIF) → WebP @ quality 82,
 *   - winner heuristic: if we did not resize and the re-encode is not smaller,
 *     keep the source.
 *
 * `sharp` (libvips) does the work. It's an optional, native dependency: if it
 * isn't installed (or fails to decode), `optimizeImage` degrades gracefully and
 * returns the source bytes unchanged with `optimized: false`, so an upload can
 * always proceed — mirroring the web codec's `usedFallback` philosophy. This
 * function therefore never fails.
 *
 * @module
 */
import { Effect } from "effect"

/** Longest-edge cap for resize — matches the web `RESIZE_LONGEST_EDGE`. */
export const DEFAULT_MAX_LONGEST_EDGE = 2000

/** WebP encode quality (0–100). Matches the web codec's 0.82. */
const WEBP_QUALITY = 82

type SharpFactory = (typeof import("sharp"))["default"]

export interface OptimizeInput {
  readonly bytes: Uint8Array
  /** Source MIME, derived from the file extension. */
  readonly sourceMime: string
}

export interface OptimizeOptions {
  /** Longest-edge cap in pixels. Defaults to {@link DEFAULT_MAX_LONGEST_EDGE}. */
  readonly maxLongestEdge?: number | undefined
  /** When true, skip all re-encoding and upload the source bytes unchanged. */
  readonly disabled?: boolean | undefined
}

export interface OptimizeResult {
  readonly bytes: Uint8Array
  /** Final content-type — may differ from the source (e.g. jpeg → webp). */
  readonly mime: string
  /** Final file extension for the R2 key. */
  readonly ext: string
  /** Natural pixel dimensions of the output, if known. */
  readonly width: number | null
  readonly height: number | null
  /** False when we shipped the source unchanged (passthrough / no gain / no sharp). */
  readonly optimized: boolean
}

type OutputFormat = "webp" | "png" | "passthrough"

/** Decide the output format from the source MIME (mirrors the web codec). */
const chooseFormat = (mime: string): OutputFormat => {
  const m = mime.toLowerCase()
  if (m === "image/svg+xml") return "passthrough"
  if (m === "image/avif") return "passthrough"
  if (m === "image/heic" || m === "image/heif") return "passthrough"
  if (m === "image/png") return "png"
  return "webp"
}

/** Best file extension for a MIME type. */
export const extForMime = (mime: string): string => {
  const m = mime.toLowerCase()
  if (m === "image/webp") return "webp"
  if (m === "image/png") return "png"
  if (m === "image/jpeg" || m === "image/jpg") return "jpg"
  if (m === "image/gif") return "gif"
  if (m === "image/svg+xml") return "svg"
  if (m === "image/avif") return "avif"
  if (m === "image/heic") return "heic"
  if (m === "image/heif") return "heif"
  if (m === "image/bmp") return "bmp"
  if (m === "image/tiff") return "tiff"
  const sub = m.split("/")[1]
  if (sub === undefined) return "bin"
  const base = sub.split("+")[0]
  return base === undefined || base.length === 0 ? "bin" : base
}

/** Map a file extension to an image MIME type, or null if not a known image. */
export const mimeFromPath = (filePath: string): string | null => {
  const dot = filePath.lastIndexOf(".")
  if (dot < 0) return null
  const ext = filePath.slice(dot + 1).toLowerCase()
  switch (ext) {
    case "png":
      return "image/png"
    case "jpg":
    case "jpeg":
      return "image/jpeg"
    case "webp":
      return "image/webp"
    case "gif":
      return "image/gif"
    case "svg":
      return "image/svg+xml"
    case "avif":
      return "image/avif"
    case "heic":
      return "image/heic"
    case "heif":
      return "image/heif"
    case "bmp":
      return "image/bmp"
    case "tiff":
    case "tif":
      return "image/tiff"
    case "ico":
      return "image/x-icon"
    default:
      return null
  }
}

/** Lazily load the optional `sharp` module; null if it isn't available. */
const loadSharp: Effect.Effect<SharpFactory | null> = Effect.tryPromise(() =>
  import("sharp"),
).pipe(
  Effect.map((mod) => mod.default),
  Effect.catch(() => Effect.succeed(null)),
)

/**
 * Optimize an image for upload. Never fails: on any problem it returns the
 * source bytes with `optimized: false`.
 */
export const optimizeImage = (
  input: OptimizeInput,
  options: OptimizeOptions = {},
): Effect.Effect<OptimizeResult> =>
  Effect.gen(function* () {
    const maxEdge = options.maxLongestEdge ?? DEFAULT_MAX_LONGEST_EDGE
    const format = chooseFormat(input.sourceMime)
    const sourceExt = extForMime(input.sourceMime)
    const sharp = yield* loadSharp

    // Read source metadata up front — dimensions are worth storing even when
    // we pass the bytes through unchanged.
    const meta =
      sharp === null
        ? null
        : yield* Effect.tryPromise(() =>
            sharp(input.bytes, { animated: true }).metadata(),
          ).pipe(Effect.catch(() => Effect.succeed(null)))

    const srcW = meta?.width ?? null
    const srcH = meta?.height ?? null
    const animated = (meta?.pages ?? 1) > 1

    const fallback = (): OptimizeResult => ({
      bytes: input.bytes,
      mime: input.sourceMime,
      ext: sourceExt,
      width: srcW,
      height: srcH,
      optimized: false,
    })

    // Ship the source unchanged when re-encoding is disabled, impossible, or
    // inappropriate for the format.
    if (
      options.disabled === true ||
      format === "passthrough" ||
      sharp === null ||
      animated ||
      srcW === null ||
      srcH === null ||
      srcW <= 0 ||
      srcH <= 0
    ) {
      return fallback()
    }

    const willResize = Math.max(srcW, srcH) > maxEdge

    // Encode with `resolveWithObject` so the stored dimensions are the encoder's
    // *actual* output (fit-inside rounding can differ from a naive computation
    // by a pixel), keeping the DB row exactly consistent with the bytes.
    const encoded = yield* Effect.tryPromise(() => {
      const base = willResize
        ? sharp(input.bytes).resize({
            width: maxEdge,
            height: maxEdge,
            fit: "inside",
            withoutEnlargement: true,
          })
        : sharp(input.bytes)
      const pipeline =
        format === "png" ? base.png() : base.webp({ quality: WEBP_QUALITY })
      return pipeline.toBuffer({ resolveWithObject: true })
    }).pipe(Effect.catch(() => Effect.succeed(null)))

    if (encoded === null) return fallback()

    // Winner heuristic: a pure format-convert that didn't shrink the file wins
    // nothing — keep the source.
    if (!willResize && encoded.data.byteLength >= input.bytes.byteLength) {
      return fallback()
    }

    return {
      bytes: encoded.data,
      mime: format === "png" ? "image/png" : "image/webp",
      ext: format === "png" ? "png" : "webp",
      width: encoded.info.width,
      height: encoded.info.height,
      optimized: true,
    }
  })
